import { assertSupabaseConfigured, supabase } from "../lib/supabase";
import type {
  CreateWorkoutPlanInput,
  PlanAssignmentInput,
  PlanStatus,
  WorkoutPlan,
  WorkoutPlanDay,
  WorkoutPlanExercise,
} from "../types/coaching";

const WORKOUT_SELECT = `
  *,
  workout_plan_days (
    *,
    workout_plan_exercises (*)
  )
`;

function sortWorkoutPlan(plan: WorkoutPlan): WorkoutPlan {
  return {
    ...plan,
    workout_plan_days: [...(plan.workout_plan_days ?? [])]
      .sort((left, right) => left.day_index - right.day_index)
      .map((day) => ({
        ...day,
        workout_plan_exercises: [...(day.workout_plan_exercises ?? [])].sort(
          (left, right) => left.sort_order - right.sort_order,
        ),
      })),
  };
}

export const workoutPlanService = {
  async listWorkoutPlansForTrainer(
    tenantId: string,
    status: PlanStatus | "all" = "all",
  ): Promise<WorkoutPlan[]> {
    assertSupabaseConfigured();

    let query = (supabase as any)
      .from("workout_plans")
      .select(WORKOUT_SELECT)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (status !== "all") query = query.eq("status", status);

    const { data, error } = await query;
    if (error) throw error;
    return ((data ?? []) as WorkoutPlan[]).map(sortWorkoutPlan);
  },

  async listWorkoutTemplatesForTrainer(tenantId: string): Promise<WorkoutPlan[]> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("workout_plans")
      .select(WORKOUT_SELECT)
      .eq("tenant_id", tenantId)
      .eq("is_template", true)
      .neq("status", "archived")
      .order("created_at", { ascending: false });

    if (error) throw error;
    return ((data ?? []) as WorkoutPlan[]).map(sortWorkoutPlan);
  },

  async createWorkoutPlan(input: CreateWorkoutPlanInput): Promise<WorkoutPlan> {
    assertSupabaseConfigured();

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: plan, error: planError } = await (supabase as any)
      .from("workout_plans")
      .insert({
        tenant_id: input.tenantId,
        created_by: user?.id ?? null,
        title: input.title.trim(),
        description: input.description?.trim() || null,
        is_template: input.isTemplate ?? true,
        status: "draft",
      })
      .select("*")
      .single();

    if (planError) throw planError;

    const { data: day, error: dayError } = await (supabase as any)
      .from("workout_plan_days")
      .insert({
        plan_id: plan.id,
        day_index: 1,
        title: input.dayTitle?.trim() || "Session 1",
        notes: input.notes?.trim() || null,
      })
      .select("*")
      .single();

    if (dayError) throw dayError;

    if (input.exerciseName?.trim()) {
      const { error: exerciseError } = await (supabase as any)
        .from("workout_plan_exercises")
        .insert({
          day_id: day.id,
          exercise_name: input.exerciseName.trim(),
          sets: input.sets?.trim() || null,
          reps: input.reps?.trim() || null,
          rest: input.rest?.trim() || null,
          notes: input.notes?.trim() || null,
          sort_order: 1,
        });

      if (exerciseError) throw exerciseError;
    }

    const created = await this.getWorkoutPlan(plan.id);
    if (!created) throw new Error("Workout plan was created but could not be loaded.");
    return created;
  },

  async getWorkoutPlan(planId: string): Promise<WorkoutPlan | null> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("workout_plans")
      .select(WORKOUT_SELECT)
      .eq("id", planId)
      .maybeSingle();

    if (error) throw error;
    return data ? sortWorkoutPlan(data as WorkoutPlan) : null;
  },

  async updateWorkoutPlan(
    planId: string,
    input: Partial<Pick<WorkoutPlan, "title" | "description" | "status">>,
  ): Promise<WorkoutPlan> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("workout_plans")
      .update(input)
      .eq("id", planId)
      .select(WORKOUT_SELECT)
      .single();

    if (error) throw error;
    return sortWorkoutPlan(data as WorkoutPlan);
  },

  async assignWorkoutPlanToClient(
    planId: string,
    clientId: string,
    options: PlanAssignmentInput = {},
  ): Promise<WorkoutPlan> {
    assertSupabaseConfigured();

    const source = await this.getWorkoutPlan(planId);
    if (!source) throw new Error("Workout plan not found.");

    if (source.is_template || source.client_id !== clientId) {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data: plan, error: planError } = await (supabase as any)
        .from("workout_plans")
        .insert({
          tenant_id: source.tenant_id,
          client_id: clientId,
          created_by: user?.id ?? source.created_by,
          title: source.title,
          description: source.description,
          status: "active",
          is_template: false,
          assigned_at: new Date().toISOString(),
          starts_on: options.startsOn || null,
          ends_on: options.endsOn || null,
        })
        .select("*")
        .single();

      if (planError) throw planError;

      for (const sourceDay of source.workout_plan_days ?? []) {
        const { data: day, error: dayError } = await (supabase as any)
          .from("workout_plan_days")
          .insert({
            plan_id: plan.id,
            day_index: sourceDay.day_index,
            title: sourceDay.title,
            notes: sourceDay.notes,
          })
          .select("*")
          .single();

        if (dayError) throw dayError;

        const exercises = (sourceDay.workout_plan_exercises ?? []).map((exercise) => ({
          day_id: day.id,
          exercise_name: exercise.exercise_name,
          sets: exercise.sets,
          reps: exercise.reps,
          rest: exercise.rest,
          notes: exercise.notes,
          sort_order: exercise.sort_order,
        }));

        if (exercises.length) {
          const { error: exerciseError } = await (supabase as any)
            .from("workout_plan_exercises")
            .insert(exercises);

          if (exerciseError) throw exerciseError;
        }
      }

      const assigned = await this.getWorkoutPlan(plan.id);
      if (!assigned) throw new Error("Assigned workout plan could not be loaded.");
      return assigned;
    }

    const { data, error } = await (supabase as any)
      .from("workout_plans")
      .update({
        client_id: clientId,
        status: "active",
        is_template: false,
        assigned_at: new Date().toISOString(),
        starts_on: options.startsOn || null,
        ends_on: options.endsOn || null,
      })
      .eq("id", planId)
      .select(WORKOUT_SELECT)
      .single();

    if (error) throw error;
    return sortWorkoutPlan(data as WorkoutPlan);
  },

  async archiveWorkoutPlan(planId: string): Promise<void> {
    assertSupabaseConfigured();

    const { error } = await (supabase as any)
      .from("workout_plans")
      .update({ status: "archived" })
      .eq("id", planId);

    if (error) throw error;
  },

  async listAssignedWorkoutPlansForClient(clientId: string): Promise<WorkoutPlan[]> {
    assertSupabaseConfigured();

    const { data, error } = await (supabase as any)
      .from("workout_plans")
      .select(WORKOUT_SELECT)
      .eq("client_id", clientId)
      .eq("status", "active")
      .order("assigned_at", { ascending: false });

    if (error) throw error;
    return ((data ?? []) as WorkoutPlan[]).map(sortWorkoutPlan);
  },
};

export type { WorkoutPlan, WorkoutPlanDay, WorkoutPlanExercise };
